<script>
// @ts-check

import BaseErrors from '@/components/base/BaseErrors.vue'
import BaseLabel from '@/components/base/BaseLabel.vue'
import { datepickerMixin } from '@/core/mixins/datepicker'
import { functions } from '@/helpers/functions'
import { postNativeMessage } from '@/helpers/mobile'
import moment from 'moment'
import Pikaday from 'pikaday'
import { mapGetters } from 'vuex'

export default {
  name: 'BaseDateRange',
  components: {
    BaseLabel,
    BaseErrors
  },
  mixins: [datepickerMixin],
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    required: {
      type: Boolean
    },
    errors: {
      /** @type {import('vue').PropType<string[]|null>} */
      type: Array,
      default: () => []
    },
    useBaseLabel: {
      type: Boolean
    },
    startMaxDate: {
      type: Date,
      default: undefined
    }
  },
  emits: ['update:modelValue'],
  data: () => ({
    startPicker: undefined,
    endPicker: undefined,
    startPickerUuid: functions.createRandomUuid(), // Uuid for mobile wrapper
    endPickerUuid: functions.createRandomUuid(), // Uuid for mobile wrapper
    setStartDate: null,
    setEndDate: null
  }),
  watch: {
    modelValue: {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue === oldValue) {
          return
        }

        /*
         * Since we don't use the default picker in the mobile application
         * if the value is updated from outside we need to set the date manually
         */
        if (this.isMobileWrapperUser) {
          if (newValue[0] !== oldValue[0]) {
            const newStartDate =
              newValue[0] && newValue[0].length > 0
                ? moment(newValue[0], 'YYYY-MM-DD').format('DD.MM.YYYY')
                : ''
            this.$refs.start.value = newStartDate
          }
          if (newValue[1] !== oldValue[1]) {
            const newEndDate =
              newValue[1] && newValue[1].length > 0
                ? moment(newValue[1], 'YYYY-MM-DD').format('DD.MM.YYYY')
                : ''
            this.$refs.end.value = newEndDate
          }
        } else {
          if (
            newValue[0] !==
              moment(this.startPicker.getDate()).format('YYYY-MM-DD') &&
            this.$refs.start
          ) {
            const settableValue = newValue[0]
              ? moment(newValue[0], 'YYYY-MM-DD').format('DD.MM.YYYY')
              : ''
            this.$refs.start.value = settableValue
          }

          if (
            newValue[1] !==
              moment(this.endPicker.getDate()).format('YYYY-MM-DD') &&
            this.$refs.end
          ) {
            const settableValue = newValue[1]
              ? moment(newValue[1], 'YYYY-MM-DD').format('DD.MM.YYYY')
              : ''
            this.$refs.end.value = settableValue
          }
        }
      }
    }
  },
  beforeUnmount() {
    if (this.isMobileWrapperUser) {
      window.removeEventListener('message', this.listenerForMobileWrapperInput)
      this.$refs.start.removeEventListener('click', this.openStartDatePicker)
      this.$refs.end.removeEventListener('click', this.openEndDatePicker)
    }
    // Remove all Pikaday related events from the DOM
    this.picker?.destroy()
  },
  mounted() {
    const pikaStart = new Pikaday({
      field: this.$refs.start,
      bound: !this.isMobileWrapperUser,
      firstDay: 1,
      format: 'DD.MM.YYYY',
      maxDate: this.startMaxDate,
      onSelect: () => {
        this.updateStartDate(this.startPicker.getDate())
      },
      i18n: this.getTranslations(),
      ariaLabel: this.$t('filters.start_date')
    })
    this.startPicker = pikaStart
    this.setStartDate = pikaStart.setDate.bind(pikaStart)

    const pikaEnd = new Pikaday({
      field: this.$refs.end,
      bound: !this.isMobileWrapperUser,
      format: 'DD.MM.YYYY',
      firstDay: 1,
      onSelect: () => {
        this.updateEndDate(this.endPicker.getDate())
      },
      i18n: this.getTranslations(),
      ariaLabel: this.$t('filters.end_date')
    })
    this.endPicker = pikaEnd
    this.setEndDate = pikaEnd.setDate.bind(pikaEnd)

    if (this.modelValue) {
      this.setStartDate(this.modelValue[0], true)
      this.setEndDate(this.modelValue[1], true)

      if (this.modelValue[0] !== undefined && this.modelValue[0] !== null) {
        const startDate = new Date(this.modelValue[0])
        this.startPicker.setStartRange(startDate)
        this.endPicker.setStartRange(startDate)
        this.endPicker.setMinDate(startDate)
      }

      if (this.modelValue[1] !== undefined && this.modelValue[1] !== null) {
        const endDate = new Date(this.modelValue[1])
        this.startPicker.setEndRange(endDate)
        this.startPicker.setMaxDate(endDate)
        this.endPicker.setEndRange(endDate)
      }
    }

    if (this.isMobileWrapperUser) {
      this.$refs.start.addEventListener('click', this.openStartDatePicker)
      this.$refs.end.addEventListener('click', this.openEndDatePicker)
      window.addEventListener('message', this.listenerForMobileWrapperInput)
      this.startPicker?.hide()
      this.endPicker?.hide()
    }
  },
  computed: {
    ...mapGetters('mobileData', ['isMobileWrapperUser']),
    /**
     * @returns {boolean}
     */
    hasValue() {
      return this.modelValue.some((item) => !!item)
    }
  },
  methods: {
    openStartDatePicker(ref) {
      ref?.target?.blur()
      let maxDate = null

      if (this.modelValue[1] !== undefined) {
        maxDate = new Date(this.modelValue[1])
      }

      const message = JSON.stringify({
        type: 'ukkopro_open_picker',
        message: {
          mode: 'date',
          uuid: this.startPickerUuid,
          value: this.modelValue[0] ? new Date(this.modelValue[0]) : new Date(),
          min_date: null,
          max_date: maxDate
        }
      })
      postNativeMessage(message)
    },
    openEndDatePicker(ref) {
      ref?.target?.blur()
      let minDate

      if (this.modelValue[0] !== undefined) {
        minDate = new Date(this.modelValue[0])
      }

      const message = JSON.stringify({
        type: 'ukkopro_open_picker',
        message: {
          mode: 'date',
          uuid: this.endPickerUuid,
          value: this.modelValue[1] ? new Date(this.modelValue[1]) : new Date(),
          min_date: minDate,
          max_date: null
        }
      })
      postNativeMessage(message)
    },
    listenerForMobileWrapperInput(message) {
      if (!message?.data?.date_time_changed?.value) {
        return
      }

      const date = message.data.date_time_changed.value
      if (message.data.date_time_changed.uuid === this.startPickerUuid) {
        const dates = [...this.modelValue]
        dates[0] = moment(date).format('YYYY-MM-DD')
        this.$emit('update:modelValue', dates)
      } else if (message.data.date_time_changed.uuid === this.endPickerUuid) {
        const dates = [...this.modelValue]
        dates[1] = moment(date).format('YYYY-MM-DD')
        this.$emit('update:modelValue', dates)
      }
    },
    updateStartDate(value) {
      const dates = [...this.modelValue]

      dates[0] = moment(value).format('YYYY-MM-DD')
      this.$emit('update:modelValue', dates)

      this.startPicker.setStartRange(value)
      this.endPicker.setStartRange(value)
      this.endPicker.setMinDate(value)
    },
    updateEndDate(value) {
      const dates = [...this.modelValue]

      dates[1] = moment(value).format('YYYY-MM-DD')
      this.$emit('update:modelValue', dates)

      this.startPicker.setEndRange(value)
      this.startPicker.setMaxDate(value)
      this.endPicker.setEndRange(value)
    },
    clearDates() {
      if (this.isMobileWrapperUser) {
        this.resetMobileDatePickers()
        return
      }

      this.setStartDate(undefined, true)
      this.startPicker.gotoToday()

      this.startPicker.setStartRange(undefined)
      this.endPicker.setStartRange(undefined)
      this.endPicker.setMinDate(undefined)

      this.setEndDate(undefined, true)
      this.endPicker.gotoToday()

      this.startPicker.setEndRange(undefined)
      this.startPicker.setMaxDate(undefined)
      this.endPicker.setEndRange(undefined)

      const dates = [undefined, undefined]
      this.$emit('update:modelValue', dates)
    },
    resetMobileDatePickers() {
      // Mobile pickers are not binded so this is needed
      this.$refs.start.value = ''
      this.$refs.end.value = ''

      const dates = [undefined, undefined]
      this.$emit('update:modelValue', dates)
    },
    focusOnStartDate() {
      const startInputRef = this.$refs.start
      startInputRef.click()
    },
    focusOnEndDate() {
      const endInputRef = this.$refs.end
      endInputRef.click()
    }
  }
}
</script>

<template>
  <div>
    <div class="daterange-picker flex flex-col">
      <BaseLabel v-if="useBaseLabel" for="daterange-start" :required>
        {{ label }}
      </BaseLabel>
      <p
        v-else
        class="mb-1 font-secondary font-normal"
        @click="focusOnStartDate"
        @keyup.enter="focusOnStartDate"
      >
        {{ label }}
      </p>
      <div
        class="daterange-input-container flex w-full rounded-md border border-secondary-300"
      >
        <div class="flex flex-1 justify-center">
          <input
            id="daterange-start"
            ref="start"
            name="daterange-start"
            :aria-label="$t('filters.start_date')"
            :placeholder="$t('entries.search_date_input_placeholder')"
            readonly
            class="w-full flex-1 rounded-l-md border-0 py-3 pr-0 pl-3 text-center font-secondary leading-tight font-normal focus:border-primary focus:shadow-outline focus:outline-hidden"
            autocomplete="off"
          />
          <button
            id="select-daterange-start"
            class="m-1 flex cursor-pointer items-center rounded-sm border-0 bg-secondary-100 px-2 text-secondary-600"
            type="button"
            :aria-label="$t('filters.select_start_date')"
            @click="focusOnStartDate"
          >
            <VIcon
              icon="$calendar-blank-outline"
              class="cursor-pointer"
              size="small"
            />
          </button>

          <input
            id="daterange-end"
            ref="end"
            name="daterange-end"
            :aria-label="$t('filters.end_date')"
            :placeholder="$t('general.search_date_input_placeholder')"
            readonly
            class="w-full flex-1 rounded-l-md border-0 py-3 pr-0 pl-3 text-center font-secondary leading-tight font-normal focus:border-primary focus:shadow-outline focus:outline-hidden"
            autocomplete="off"
          />
          <button
            id="select-daterange-end"
            class="m-1 flex cursor-pointer items-center rounded-sm border-0 bg-secondary-100 px-2 text-secondary-600"
            type="button"
            :aria-label="$t('filters.select_end_date')"
            @click="focusOnEndDate"
          >
            <VIcon
              icon="$calendar-blank-outline"
              class="cursor-pointer"
              size="small"
            />
          </button>
          <div v-if="hasValue" class="flex items-center">
            <button
              class="m-1 flex items-center rounded-full bg-secondary-200 fill-current p-1 text-black"
              :aria-label="$t('general.clear')"
              type="button"
              @click="clearDates"
            >
              <VIcon icon="$close" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <BaseErrors :errors :label class="mt-2" />
  </div>
</template>
